import React from 'react';

interface NewBasicInfoProps {
  form: any;
  projects: any[];
  referenceData: any;
  onChange: (field: string, value: any) => void;
}

export function NewBasicInfo({ form, projects, referenceData, onChange }: NewBasicInfoProps) {
  const typeToCategoryMap: Record<string, string> = {
    'construction': 'Thi-Cong-Canh-Quan',
    'maintenance': 'Bao-Duong-Canh-Quan',
    'other': 'Cong-Viec-Khac'
  };

  const categories = referenceData?.categories || [];
  
  // Find the root category for the current work type (Level 0)
  const rootCategory = categories.find((cat: any) => {
    const targetCode = typeToCategoryMap[form.work_type]?.toLowerCase();
    const catCode = (cat.code || '').toLowerCase().trim();
    const catName = (cat.label || '').toLowerCase().trim();
    
    // Match by code or fallback to name if needed (though code is preferred)
    return catCode === targetCode || (targetCode && catName.includes(targetCode.replace(/-/g, ' ')));
  });
  
  // Level 1: Direct children of root
  const level1Categories = categories.filter((cat: any) => 
    rootCategory && cat.parent_id === rootCategory.id
  );

  // Level 2: Direct children of selected Level 1
  const level2Categories = categories.filter((cat: any) => 
    form.category_id && cat.parent_id === form.category_id
  );

  return (
    <div className="bg-white p-3 rounded-md border border-slate-100 space-y-4">
      <div className="flex items-center gap-2 border-b border-slate-50 pb-2">
        <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs">1</div>
        <h2 className="sm:text-sm text-xs text-slate-800">Thông tin công việc</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="sm:text-sm text-xs text-slate-500">Tên công việc <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type="text"
                placeholder="Nhập tên công việc"
                className="w-full h-9 px-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
                value={form.title}
                onChange={(e) => onChange('title', e.target.value)}
                maxLength={200}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 sm:text-sm text-xs text-slate-400">{form.title?.length || 0}/200</span>
            </div>
          </div>

          <div className="space-y-1">
            <label className="sm:text-sm text-xs text-slate-500">Dự án <span className="text-red-500">*</span></label>
            <select
              className="w-full h-9 px-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
              value={form.project_id}
              onChange={(e) => onChange('project_id', e.target.value)}
            >
              <option value="">Chọn dự án</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>

          <div className="space-y-1">
            <label className="sm:text-sm text-xs text-slate-500">Loại công việc <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              <TypeButton 
                active={form.work_type === 'construction'} 
                onClick={() => {
                  onChange('work_type', 'construction');
                  onChange('category_id', '');
                  onChange('sub_category_id', '');
                }}
                icon="ri-building-line"
                title="Thi công"
                desc="Giai đoạn thi công dự án"
              />
              <TypeButton 
                active={form.work_type === 'maintenance'} 
                onClick={() => {
                  onChange('work_type', 'maintenance');
                  onChange('category_id', '');
                  onChange('sub_category_id', '');
                }}
                icon="ri-shield-check-line"
                title="Bảo dưỡng"
                desc="Kế hoạch bảo dưỡng định kỳ"
              />
              <TypeButton 
                active={form.work_type === 'other'} 
                onClick={() => {
                  onChange('work_type', 'other');
                  onChange('category_id', '');
                  onChange('sub_category_id', '');
                }}
                icon="ri-more-line"
                title="Khác"
                desc="Các loại công việc ngoài luồng"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-1 h-full flex flex-col">
            <label className="sm:text-sm text-xs text-slate-500">Mô tả công việc</label>
            <div className="relative flex-1">
              <textarea
                placeholder="Nhập mô tả chi tiết công việc (nếu có)"
                className="w-full h-full min-h-[120px] p-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50 resize-none"
                value={form.description}
                onChange={(e) => onChange('description', e.target.value)}
                maxLength={1000}
              />
              <span className="absolute right-2 bottom-2 sm:text-sm text-xs text-slate-400">{form.description?.length || 0}/1000</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="sm:text-sm text-xs text-slate-500">Hạng mục</label>
          <select 
            className="w-full h-9 px-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
            value={form.category_id}
            onChange={(e) => {
              onChange('category_id', e.target.value);
              onChange('sub_category_id', '');
            }}
          >
            <option value="">Chọn hạng mục công việc</option>
            {level1Categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="sm:text-sm text-xs text-slate-500">Hạng mục chi tiết</label>
          <select 
            className="w-full h-9 px-3 rounded-md border border-slate-200 sm:text-sm text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-slate-50/50"
            value={form.sub_category_id}
            onChange={(e) => onChange('sub_category_id', e.target.value)}
            disabled={!form.category_id}
          >
            <option value="">Chọn hạng mục chi tiết</option>
            {level2Categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function TypeButton({ active, onClick, icon, title }: any) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-2 sm:p-3 rounded-md border transition-all ${
        active 
          ? 'border-emerald-600 bg-emerald-50 ' 
          : 'border-slate-100 bg-slate-50/50 hover:bg-slate-50'
      }`}
    >
      <div className="flex flex-col lg:flex-row items-center justify-center lg:justify-start gap-1 lg:gap-2">
        <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
          <i className={`${icon} sm:text-sm text-xs`} />
        </div>
        <span className={`text-xs sm:text-sm text-center lg:text-left ${active ? 'text-emerald-600' : 'text-slate-700'}`}>{title}</span>
      </div>
    </button>
  );
}
